import { z } from "zod";

import http from "@/services/http";
import unauthenticatedHttp from "@/services/unauthenticatedHttp";
import isUnauth from "@/utils/isUnauth";

const metaData = z
  .object({
    current_page: z.number(),
    total_pages: z.number(),
    total_count: z.number(),
    pageSize: z.number(),
    previous_page_url: z.string().nullish(),
    next_page_url: z.string().nullish(),
  })
  .nullish();

const prepMaterial = z.object({
  id: z.number(),
  exam_id: z.number().nullish(),
  guid: z.string().nullish(),
  created_at: z.iso.datetime().nullish(),
  updated_at: z.iso.datetime().nullish(),
  deleted_at: z.iso.datetime().nullish(),
  created_by: z.string().nullish(),
  updated_by: z.string().nullish(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  url: z.string().nullish(),
  type: z.string().nullish(),
  SortOrder: z.number().nullish(),
});

const Topic = z.object({
  id: z.number(),
  created_at: z.iso.datetime().nullish(),
  updated_at: z.iso.datetime().nullish(),
  deleted_at: z.iso.datetime().nullish(),
  created_by: z.string().nullish(),
  updated_by: z.string().nullish(),
  topic_name: z.string().nullish(),
  guid: z.string().nullish(),
  exam_id: z.number().nullish(),
  weight: z.number().nullish(),
});

const Thread = z.object({
  URL: z.string().nullish(),
  Title: z.string().nullish(),
  Body: z.string().nullish(),
  Id: z.string().nullish(),
});

const Community = z.object({
  url: z.string().nullish(),
  name: z.string().nullish(),
  id: z.string().nullish(),
  followers: z.number().nullish(),
  category: z.string().nullish(),
  articles: z.number().nullish(),
  unanswered_questions: z.number().nullish(),
  posts: z.number().nullish(),
  threads: z.array(Thread).nullish(),
});

const Exam = z.object({
  id: z.number(),
  created_at: z.iso.datetime().nullish(),
  updated_at: z.iso.datetime().nullish(),
  deleted_at: z.iso.datetime().nullish(),
  created_by: z.string().nullish(),
  updated_by: z.string().nullish(),
  certification_id: z.number().nullish(),
  exam_number: z.string().nullish(),
  exam_id: z.number().nullish(),
  guid: z.string().nullish(),
  acronym: z.string().nullish(),
  version: z.string().nullish(),
  title: z.string().nullish(),
  release_date: z.iso.datetime().nullish(),
  eol_date: z.iso.datetime().nullish(),
  duration_sec: z.number().nullish(),
  summary: z.string().nullish(),
  description: z.string().nullish(),
  type: z.string().nullish(),
  topics: z.array(Topic).nullish(),
  prescribed_prep_material: z.array(prepMaterial).nullish(),
});

export const Certification = z
  .object({
    id: z.number(),
    created_at: z.iso.datetime().nullish(),
    updated_at: z.iso.datetime().nullish(),
    deleted_at: z.iso.datetime().nullish(),
    created_by: z.string().nullish(),
    updated_by: z.string().nullish(),
    name: z.string().nullish(),
    acronym: z.string().nullish(),
    summary: z.string().nullish(),
    description: z.string().nullish(),
    active: z.number().nullish(),
    sort_order: z.number().nullish(),
    technology: z.array(z.string()).nullish(),
    certification_level: z.string().nullish(),
    ce_credits: z.number().nullish(),
    guid: z.string().nullish(),
    job_roles: z.array(z.string()).nullish(),
    skills_associated: z.array(z.string()).nullish().or(z.string().default("").nullish()),
    type: z.string().nullish(),
    publisher_name: z.string().nullish(),
    language: z.string().nullish(),
    community_id: z.string().nullish(),
    community_name: z.string().nullish(),
    community_description: z.string().nullish(),
    tag_line: z.string().nullish(),
    justification: z.string().nullish(),
    exam: z.array(Exam).nullish(),
    friendly_url: z.string().nullish().optional(),
    locale_code: z.string().nullish().optional(),
    community: Community.nullish().optional(),
  })
  .loose();

export const certificationsResponse = z
  .object({
    data: z.array(Certification),
    metadata: metaData,
    total_records: z.number(),
    timestamp: z.iso.datetime(),
    version: z.string(),
  })
  .loose();

export async function fetchById(id: any) {
  const { data } = await http().get(
    `${globalThis.env.API_CERTIFICATION}/certifications/${id}?includeExams=true`
  );
  return Certification.parse(data);
}

export async function fetchAll() {
  if (isUnauth()) {
    const { data } = await unauthenticatedHttp().get(globalThis.env.API_UNAUTH + "/certifications");
    return data;
  } else if (globalThis.env.API_CERTIFICATION) {
    const { data } = await http().get(`${globalThis.env.API_CERTIFICATION}/certifications`);
    return certificationsResponse.parse(data);
  } else {
    return {};
  }
}

export async function fetchLearnCertifications() {
  if (isUnauth()) {
    const { data } = await unauthenticatedHttp().get(
      globalThis.env.API_UNAUTH + "/learn/certifications"
    );
    return data;
  } else if (globalThis.env.API_CERTIFICATION) {
    const { data } = await http().get(`${globalThis.env.API_CERTIFICATION}/learn/certifications`);
    return certificationsResponse.parse(data);
  } else {
    return {};
  }
}

export async function searchCertifications(guid: string[]) {
  if (isUnauth()) {
    const { data } = await unauthenticatedHttp().post(
      `${globalThis.env.API_UNAUTH}/certifications/query`,
      {
        guid: guid,
      }
    );
    return data;
  }
  const { data } = await http().post(`${globalThis.env.API_CERTIFICATION}/certifications/query`, {
    guid: guid,
  });
  return certificationsResponse.parse(data);
}

export async function fetchCertificationBy(options: { name?: string; guid?: string }) {
  if (!options.name && !options.guid) {
    throw new Error("Either name or guid must be provided");
  }

  const params = {
    ...(options.name ? { name: options.name } : { guid: options.guid }),
    include_community: true,
  };

  const apiUrl = isUnauth()
    ? `${globalThis.env.API_UNAUTH}/certifications`
    : `${globalThis.env.API_CERTIFICATION}/certifications`;

  const httpClient = isUnauth() ? unauthenticatedHttp() : http();
  const { data } = await httpClient.get(apiUrl, { params });

  // Parse response with certificationsResponse schema
  const parsedResponse = certificationsResponse.parse(data);

  // Return first certification from data array or null
  return parsedResponse.data.length > 0 ? parsedResponse.data[0] : null;
}
